import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FirebaseCrashlytics } from '@capacitor-firebase/crashlytics';
import { Capacitor } from '@capacitor/core';
import { from, Observable, of, TimeoutError } from 'rxjs';
import { catchError, map, switchMap, tap, timeout } from 'rxjs/operators';
import * as StackTrace from 'stacktrace-js';
import { LoggingService } from '../shared/logging.service';
import { OfflineNotificationService } from './offline-notification.service';
import { StorageService } from './storage.service';
import { UserEventWriteService } from './user-event.write.service';

const DELAI_MAX_LECTURE_SERVEUR_MS = 30_000;

@Injectable({ providedIn: 'root' })
export abstract class AbstractReadService<T> {
  constructor(
    private readonly http: HttpClient,
    private readonly storage: StorageService,
    private readonly offlineService: OfflineNotificationService,
    private readonly userEventWriteService: UserEventWriteService,
    private readonly log: LoggingService
  ) {}

  public fetch(param: any = null): Observable<T> {
    // FIRST : try to write
    // refresh the internal value if possible, then return the internal value
    return this.userEventWriteService.tryToRefresh().pipe(
      switchMap(() => this.refreshLocalValue(param)),
      switchMap(() => this.getLocally(param))
    );
  }

  private refreshLocalValue(param: any = null): Observable<any> {
    return this.tryToGetFromServer(param).pipe(
      tap(() => this.offlineService.setOnline()),
      switchMap(res => this.saveLocally(res, param)),
      catchError(err => {
        if (err.status === 404) {
          // server not found = novalue
          this.removeLocally(param);
          this.offlineService.setOnline();
          this.log.error(
            `Lecture de ${this.getStorageKey()} refusée par le serveur (404) : le cache local est purgé, l'écran n'aura ` +
              `plus de données. Vérifier que la ressource existe encore côté backend.`,
            err
          );
          this.reportError(err, param);
        } else if (err instanceof TimeoutError) {
          this.offlineService.setOffline();
          this.log.error(
            `Lecture de ${this.getStorageKey()} sans réponse après ${DELAI_MAX_LECTURE_SERVEUR_MS / 1000} s : abandonnée, les données ` +
              `du cache local sont utilisées et le bandeau hors ligne est affiché. Vérifier le réseau de l'agent ou la latence du backend.`,
            err
          );
          this.reportError(err, param);
        } else if (this.estHorsLigne(err)) {
          // on est offline
          this.offlineService.setOffline();
          this.log.warn(
            `Lecture de ${this.getStorageKey()} impossible, appareil hors ligne : les données du cache local sont ` +
              `utilisées et la lecture repartira au retour du réseau.`
          );
        } else {
          this.offlineService.setOffline();
          this.log.error(
            `Lecture de ${this.getStorageKey()} en échec (status ${err.status}) : les données du cache local sont ` +
              `utilisées, elles peuvent être périmées. Vérifier l'état du backend.`,
            err
          );
          this.reportError(err, param);
        }
        // everything is allright
        return of({});
      })
    );
  }

  private estHorsLigne(err: any): boolean {
    return err?.status === undefined || err.status === 0;
  }

  private tryToGetFromServer(param: any = null): Observable<T> {
    return this.http.get<T>(this.getResourceURL(param), { observe: 'response' }).pipe(
      timeout(DELAI_MAX_LECTURE_SERVEUR_MS),
      map(res => res.body),
      map(res => this.hydrate(res))
    );
  }

  protected abstract getResourceURL(param: any): string;

  public saveLocally(value: T, param?: any): Promise<any> {
    return this.storage.set(this.getStorageKey(this.paramToString(param)), JSON.stringify(value));
  }

  protected removeLocally(param?: any): Promise<any> {
    return this.storage.remove(this.getStorageKey(this.paramToString(param)));
  }

  protected getLocally(param?: any): Observable<T> {
    return from(this.storage.get(this.getStorageKey(this.paramToString(param)))).pipe(map(c => this.hydrateFromString(c)));
  }

  protected abstract hydrate(obj: any): T;

  protected hydrateFromString(obj: string): T {
    return obj ? this.hydrate(JSON.parse(obj)) : (null as T);
  }

  protected abstract getStorageKey(stringifiedParam?: string): string;

  private paramToString(param?: any): string {
    if (!param) {
      return '';
    }
    return typeof param === 'string' ? param : param instanceof String ? param.toString() : JSON.stringify(param);
  }

  private async reportError(error: any, param: any): Promise<void> {
    if (Capacitor.isNativePlatform()) {
      if (typeof error === 'string') {
        return FirebaseCrashlytics.recordException({
          message: error,
        }).then(() => FirebaseCrashlytics.sendUnsentReports());
      } else if (error instanceof HttpErrorResponse) {
        return FirebaseCrashlytics.recordException({
          message: error.message,
        }).then(() => FirebaseCrashlytics.sendUnsentReports());
      } else {
        StackTrace.fromError(error)
          .then(stacktrace => {
            return FirebaseCrashlytics.recordException({
              message: error.message,
              stacktrace: error.stack,
            });
          })
          .catch(() => {
            this.log.error('Unparsable', error);
          })
          .then(() => FirebaseCrashlytics.sendUnsentReports());
      }
    } else {
      this.log.error('Une erreur est survenue durant la lecture', error);
    }
  }
}
